/**
 * useModal.js
 * Global modal state hook — share between SignalCards, RimlandMap, and Modal.
 *
 * Usage:
 *   const { openModal, closeModal, modalEvent } = useModal();
 *
 * Components dispatch via window events so they don't need to share a prop chain.
 * Modal listens for 'rimland:openModal' events.
 */

import { useState, useEffect } from 'react';
import { SIGNALS } from '../data/signalData';

// Flatten all events for lookup by id
const EVENT_MAP = {};
Object.values(SIGNALS).forEach(card => {
  if (card.lead) EVENT_MAP[card.lead.id] = { ...card.lead, cardLabel: card.label };
  (card.events || []).forEach(ev => {
    EVENT_MAP[ev.id] = { ...ev, cardLabel: card.label };
  });
});

// Special non-event modal types
const SPECIAL_MODALS = {
  briefing: {
    id: 'briefing',
    cardLabel: 'ADVISORY',
    title: 'Request a Strategic Briefing',
    summary: 'IndUS Tech provides tailored strategic intelligence briefings for defense investors, business executives, and government officials active in the U.S.-India corridor. Contact us to discuss retainer advisory engagements.',
    time: '',
    source: 'IndUS Tech',
  },
};

export function useModal() {
  const [modalEvent, setModalEvent] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const id = e.detail;
      const event = EVENT_MAP[id] || SPECIAL_MODALS[id] || null;
      setModalEvent(event);
    };
    window.addEventListener('rimland:openModal', handler);
    return () => window.removeEventListener('rimland:openModal', handler);
  }, []);

  const openModal = (id) => {
    window.dispatchEvent(new CustomEvent('rimland:openModal', { detail: id }));
  };

  const closeModal = () => setModalEvent(null);

  return { modalEvent, openModal, closeModal };
}
